import { useEffect, useMemo } from "react";
import { useContext } from "react";
import { Receipt } from "lucide-react";
import { useLiteSettledDashboardStatistics } from "@/hooks/tiktokSkuSettledPeriod.hooks";
import { UserStateContext } from "@/contexts/UserStateProvider";
import { collectSettledTotals } from "@/lib/utils";
import { ChartTile } from "@/types/ChartTile";
import { Badge } from "@/components/ui/badge";
import { ChartTileOverview } from "@/components/chart-tile";
import { TaxSettingsPopover } from "@/components/tax-settings-popover";

export default function TaxOverviewTile({
  selectedDateRange,
  fromDate,
  toDate,
  headerExtra,
  extraRightHeader,
  overrideTiles,
}: {
  selectedDateRange: string;
  fromDate: Date;
  toDate: Date;
  headerExtra?: React.ReactNode;
  extraRightHeader?: React.ReactNode;
  overrideTiles?: {
    dataKey: string;
    gridClass: string;
    hide?: boolean;
    color?: string;
    chartColor?: string;
  }[];
}) {
  const { data: liteSettledDashboardStatistics, isLoading: isSettledLoading } =
    useLiteSettledDashboardStatistics(
      fromDate,
      toDate,
      true,
      undefined,
      "byStatementTime"
    );
  const { currency, swapChartsOnDashboard } = useContext(UserStateContext);

  const settledTotals = useMemo(
    () => collectSettledTotals(liteSettledDashboardStatistics),
    [liteSettledDashboardStatistics]
  );

  const dashboardTiles: ChartTile[] = [
    {
      title: "Sales VAT",
      tooltip: "Sales VAT collected on settled sales.",
      value:
        settledTotals && settledTotals.settledTax
          ? `${currency.symbol}${settledTotals.settledTax?.toLocaleString(
              undefined,
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}`
          : `${currency.symbol}0.00`,
      change: 5,
      icon: Receipt,
      color: "from-red-400 to-red-600",
      chartColor: "#EF4444",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "settledTax",
      gridClass: "col-span-2",
    },
    {
      title: "Purchases VAT Refund",
      tooltip: "Sales VAT that can be reclaimed from settled purchases.",
      value:
        settledTotals && settledTotals.settledTaxBack
          ? `${currency.symbol}${settledTotals.settledTaxBack?.toLocaleString(
              undefined,
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}`
          : `${currency.symbol}0.00`,
      change: 5,
      icon: Receipt,
      color: "from-green-400 to-green-600",
      chartColor: "#10B981",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "settledTaxBack",
      gridClass: "col-span-2",
    },
  ].filter((tile) => {
    if (!overrideTiles) return true;
    const override = overrideTiles.find((o) => o.dataKey === tile.dataKey);
    if (!override) return false;
    tile.color = override.color || tile.color;
    tile.chartColor = override.chartColor || tile.chartColor;
    tile.gridClass = override.gridClass;
    return true;
  });

  return (
    <div className="space-y-2">
      <div className="mb-4">
        <div className="flex justify-between gap-4 items-center">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-foreground">
              Tax Overview
            </h3>
            <Badge className="bg-orange-500/20 text-orange-500">
              ~ Estimated
            </Badge>
            {headerExtra}
          </div>
          <div className="flex items-center gap-4">
            {extraRightHeader && extraRightHeader}
          </div>
        </div>
        <TaxSettingsPopover type="CONFIGURE" />
      </div>

      <div className="pb-10 flex flex-col gap-4">
        {dashboardTiles.map((tile, index) =>
          tile.hide ? null : (
            <ChartTileOverview
              key={index}
              tile={tile}
              swapCharts={swapChartsOnDashboard}
              isLoading={isSettledLoading}
              isSettledLoading={isSettledLoading}
              combinedData={liteSettledDashboardStatistics}
            />
          )
        )}
      </div>
    </div>
  );
}
